import React, { useState, useEffect } from 'react';
import { findByIdentityWithManager } from '../../services/identity';
import { useNavigate } from 'react-router-dom';
import { Expense, ExpenseStatus, ExpenseConnection, PaginationParams } from '../../types/expense';
import { ExpenseService } from '../../services/expense';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { notifyExpenseSubmitted, notifyExpenseDeleted } from '../../services/inAppNotification';
import { emailExpenseSubmitted, emailExpenseDeleted } from '../../services/emailNotification';
import { getFinanceAdminEmails } from '../../services/userLoader';
import { useExpenseListSubscriptions } from '../../hooks/useSubscriptions';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../common/Pagination';
import './ExpenseList.css';

interface ExpenseListProps {
  expenses?: Expense[]; // For backward compatibility
  expenseConnection?: ExpenseConnection; // New paginated data
  onExpenseSelect?: (expense: Expense) => void;
  showActions?: boolean;
  onExpenseUpdate?: () => void;
  enablePagination?: boolean;
  onLoadMore?: (pagination: PaginationParams) => void;
  loading?: boolean;
  enableRealTimeUpdates?: boolean; // New prop for subscription control
}

/**
 * Expense List Component with Pagination Support and Real-time Updates
 * Requirements: 6.4 - Expense list with filtering capabilities
 * Requirements: 10.4 - Efficient pagination for large expense lists
 * Requirements: 9.3 - Real-time expense status updates
 */
const ExpenseList: React.FC<ExpenseListProps> = ({ 
  expenses: legacyExpenses,
  expenseConnection,
  onExpenseSelect, 
  showActions = false,
  onExpenseUpdate,
  enablePagination = false,
  onLoadMore,
  loading = false,
  enableRealTimeUpdates = true
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [localExpenses, setLocalExpenses] = useState<Expense[]>([]);

  // Use paginated data if available, otherwise fall back to legacy expenses array
  const expenses = expenseConnection?.items || legacyExpenses || localExpenses;
  const hasNextPage = !!expenseConnection?.nextToken;
  const hasPreviousPage = currentPage > 1;
  const totalItems = expenseConnection?.total;

  // Handle real-time expense updates
  const handleExpenseUpdate = (updatedExpense: Expense) => {
    // Update local state if we're managing expenses locally
    if (!expenseConnection && !legacyExpenses) {
      setLocalExpenses(prev => {
        const index = prev.findIndex(exp => exp.id === updatedExpense.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = updatedExpense;
          return updated;
        } else {
          // New expense, add to the beginning
          return [updatedExpense, ...prev];
        }
      });
    }

    // Notify parent component
    if (onExpenseUpdate) {
      onExpenseUpdate();
    }
  };

  // Set up real-time subscriptions
  const { isSubscribed } = useExpenseListSubscriptions(
    user,
    handleExpenseUpdate,
    enableRealTimeUpdates
  );

  const formatAmount = (amount: number): string => {
    return ExpenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusColor(status);
  };

  const getStatusDescription = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusDescription(status);
  };

  const handleExpenseClick = (expense: Expense) => {
    if (onExpenseSelect) {
      onExpenseSelect(expense);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && onLoadMore && expenseConnection?.nextToken) {
      setCurrentPage(prev => prev + 1);
      onLoadMore({
        limit: itemsPerPage,
        nextToken: expenseConnection.nextToken,
        sortDirection: 'DESC'
      });
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && onLoadMore) {
      setCurrentPage(prev => prev - 1);
      // For previous page, we need to implement a different strategy
      // since DynamoDB cursor-based pagination doesn't support going backwards
      // For now, we'll reload from the beginning and stop at the right page
      onLoadMore({
        limit: itemsPerPage * (currentPage - 1),
        sortDirection: 'DESC'
      });
    }
  };

  const handlePageChange = (page: number) => {
    if (onLoadMore && page !== currentPage) {
      setCurrentPage(page);
      onLoadMore({
        limit: itemsPerPage * page,
        sortDirection: 'DESC'
      });
    }
  };

  const handleEdit = (expense: Expense, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/expenses/${expense.id}/edit`);
  };

  const handleDelete = async (expense: Expense, event: React.MouseEvent) => {
    event.stopPropagation();
    const confirmMsg = expense.status === ExpenseStatus.SUBMITTED
      ? 'This expense has been submitted. Are you sure you want to delete it? Your manager will be notified.'
      : 'Are you sure you want to delete this expense? This action cannot be undone.';
    if (window.confirm(confirmMsg)) {
      try {
        const wasSubmitted = expense.status === ExpenseStatus.SUBMITTED;
        await ExpenseServiceWrapper.deleteDraft(expense.id);

        // Notify manager if the expense was in Submitted status
        if (wasSubmitted) {
          let managerEmail = user?.managerEmail;
          if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
            try {
              const response = await CognitoAdminService.listUsers();
              const cognitoUser = findByIdentityWithManager(response.users, user?.email);
              if (cognitoUser?.managerEmail) {
                managerEmail = cognitoUser.managerEmail;
              }
            } catch (err) {
              console.warn('[ExpenseList] Could not fetch manager email:', err);
            }
          }
          if (managerEmail) {
            const employeeName = user?.name || 'Employee';
            try {
              await notifyExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
              await emailExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
              console.log('[ExpenseList] Manager notified about expense deletion');
            } catch (notifyErr) {
              console.warn('[ExpenseList] Could not notify manager about deletion:', notifyErr);
            }
          }
        }

        if (onExpenseUpdate) {
          onExpenseUpdate();
        }
      } catch (error) {
        console.error('Failed to delete expense:', error);
        alert('Failed to delete expense. Please try again.');
      }
    }
  };

  const handleSubmit = async (expense: Expense, event: React.MouseEvent) => {
    event.stopPropagation();
    const validation = ExpenseService.validateForSubmission(expense);
    
    if (!validation.isValid) {
      alert(`Cannot submit expense:\n${validation.errors.join('\n')}`);
      return;
    }

    if (window.confirm('Are you sure you want to submit this expense for approval?')) {
      try {
        // Get manager email - if not in user context, fetch from Cognito Admin API
        let managerEmail = user?.managerEmail;
        if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
          try {
            console.log('[ExpenseList] Fetching manager email from Cognito for:', user.email);
            const response = await CognitoAdminService.listUsers();
            const cognitoUser = findByIdentityWithManager(response.users, user?.email);
            if (cognitoUser?.managerEmail) {
              managerEmail = cognitoUser.managerEmail;
              console.log('[ExpenseList] Found manager email:', managerEmail);
            }
          } catch (err) {
            console.warn('[ExpenseList] Could not fetch manager email from Cognito:', err);
          }
        }

        // Pass user info for workflow routing (bypass manager if no manager assigned)
        const updatedExpense = await ExpenseServiceWrapper.submitForApproval(expense.id, {
          role: user?.role,
          managerEmail: managerEmail
        });

        // Send notifications based on the new status
        const employeeName = user?.name || 'Employee';

        if (updatedExpense.status === ExpenseStatus.SUBMITTED && managerEmail) {
          // Expense needs manager approval - notify manager
          console.log('[ExpenseList] Sending notification to manager:', managerEmail);
          try {
            await notifyExpenseSubmitted(managerEmail, employeeName, expense.title, expense.amount, expense.id);
            await emailExpenseSubmitted(managerEmail, employeeName, expense.title, expense.amount, expense.id);
            console.log('[ExpenseList] Manager notification sent successfully');
          } catch (notifyErr) {
            console.error('[ExpenseList] Failed to notify manager:', notifyErr);
          }
        } else if (updatedExpense.status === ExpenseStatus.MANAGER_APPROVED) {
          // Expense bypassed manager - notify finance admins
          console.log('[ExpenseList] Expense bypassed manager, notifying finance admins');
          try {
            const financeAdminEmails = await getFinanceAdminEmails();
            console.log('[ExpenseList] Finance admin emails found:', financeAdminEmails);
            
            if (financeAdminEmails.length === 0) {
              console.warn('[ExpenseList] No finance admin emails found! Check user roles.');
            }
            
            for (const adminEmail of financeAdminEmails) {
              console.log('[ExpenseList] Notifying finance admin:', adminEmail);
              await notifyExpenseSubmitted(adminEmail, employeeName, expense.title, expense.amount, expense.id);
              await emailExpenseSubmitted(adminEmail, employeeName, expense.title, expense.amount, expense.id);
            }
            console.log('[ExpenseList] Finance admin notifications sent successfully');
          } catch (notifyErr) {
            console.error('[ExpenseList] Could not notify finance admins:', notifyErr);
          }
        }

        if (onExpenseUpdate) {
          onExpenseUpdate();
        }
      } catch (error) {
        console.error('Failed to submit expense:', error);
        alert('Failed to submit expense. Please try again.');
      }
    }
  };

  if (expenses.length === 0) {
    return (
      <div className="expense-list">
        <div className="expense-list-header">
          {enableRealTimeUpdates && (
            <div className="subscription-status">
              <span className={`status-indicator ${isSubscribed ? 'connected' : 'disconnected'}`}>
                {isSubscribed ? '🟢 Live' : '🔴 Offline'}
              </span>
            </div>
          )}
        </div>
        <div className="empty-state">
          <h3>No expenses found</h3>
          <p>You haven't created any expenses yet, or no expenses match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="expense-list">
      <div className="expense-list-header">
        <span className="expense-count">{expenses.length} expense{expenses.length !== 1 ? 's' : ''}</span>
        {enableRealTimeUpdates && (
          <div className="subscription-status">
            <span className={`status-indicator ${isSubscribed ? 'connected' : 'disconnected'}`}>
              {isSubscribed ? '🟢 Live Updates' : '🔴 No Live Updates'}
            </span>
          </div>
        )}
      </div>
      
      <div className="expense-items">
        {expenses.map(expense => (
          <div 
            key={expense.id} 
            className={`expense-item ${onExpenseSelect ? 'clickable' : ''}`}
            onClick={() => handleExpenseClick(expense)}
          >
            <div className="expense-main">
              <div className="expense-header">
                <h3 className="expense-title">{expense.title}</h3>
                <div className={`expense-status ${getStatusColor(expense.status)}`}>
                  {expense.status}
                </div>
              </div>
              
              <div className="expense-details">
                <div className="expense-amount">{formatAmount(expense.amount)}</div>
                <div className="expense-category">{expense.category}</div>
                <div className="expense-date">{formatDate(expense.date)}</div>
                <div className="expense-payment-method">{expense.paymentMethod}</div>
              </div>
              
              {/* Submitter and Manager Info */}
              {((expense as any).submitterName || (expense as any).managerName) && (
                <div className="submitter-info">
                  {(expense as any).submitterName && (
                    <span className="submitter">
                      👤 Submitted by: <strong>{(expense as any).submitterName}</strong>
                    </span>
                  )}
                  {(expense as any).managerName && (
                    <span className="manager">
                      👔 Manager: <strong>{(expense as any).managerName}</strong>
                    </span>
                  )}
                </div>
              )}
              
              {expense.description && (
                <div className="expense-description">{expense.description}</div>
              )}
              
              <div className="expense-meta">
                <span className="expense-receipts">
                  {expense.receipts?.length || 0} receipt{(expense.receipts?.length || 0) !== 1 ? 's' : ''}
                </span>
                <span className="expense-created">
                  Created {formatDate(expense.createdAt)}
                </span>
              </div>
            </div>
            
            {showActions && (
              <div className="expense-actions">
                {ExpenseService.canEdit(expense) && (
                  <button 
                    className="action-button edit"
                    onClick={(e) => handleEdit(expense, e)}
                    title="Edit expense"
                  >
                    Edit
                  </button>
                )}
                
                {ExpenseService.canSubmit(expense) && (
                  <button 
                    className="action-button submit"
                    onClick={(e) => handleSubmit(expense, e)}
                    title="Submit for approval"
                  >
                    Submit
                  </button>
                )}
                
                {ExpenseService.canDelete(expense) && (
                  <button 
                    className="action-button delete"
                    onClick={(e) => handleDelete(expense, e)}
                    title="Delete draft"
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {enablePagination && onLoadMore && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageChange={handlePageChange}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          loading={loading}
        />
      )}
    </div>
  );
};

export default ExpenseList;